import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
const { notifSend } = notifActions;
import { IAddImageDataAction, IAddImageData } from 'models/common/addimage';
import { IImageData, ICropImageData } from 'models/common';
import { userCardSuccess } from '../user/usercard';
import { userPreferencesSuccess } from '../user/userpreferences';
import { closeModal } from '../modal';
import * as c from 'models/constants';
import { shopCardSuccess } from '../shop/shopcard';
import { change } from 'redux-form';

/** Action Types */
export const REQUEST_START: string = 'addimage/REQUEST_START';
export const REQUEST_FAILURE: string = 'addimage/REQUEST_FAILURE';
export const ADD_IMAGE_SUCCESS: string = 'addimage/UPDATE_POLL_SUCCESS';
export const RESET_DATA: string = 'addimage/RESET_DATA';
export const CLEAR_FORM_DATA: string = 'addimage/CLEAR_FORM_DATA';
export const CLEAR_FORM_ERROR: string = 'addimage/CLEAR_FORM_ERROR';

/** Initial State */
const initialState: IAddImageData = {
	isFetching: false,
	image: {
		id: '',
		href: '',
		author: '',
		description: '',
	},
};

/** Reducer */
export function imageReducer(state = initialState, action: IAddImageDataAction): IAddImageData {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case ADD_IMAGE_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				image: action.payload.image,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				image: {
					id: '',
					href: '',
					author: '',
					description: '',
				},
			});

		case CLEAR_FORM_ERROR:
			return Object.assign({}, state, {
				isFetching: false,
				message: null,
				error: false,
			});

		default:
			return state;
	}
}

/** Action Creator  */
export function updateProfileImage(image: ICropImageData) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		let url: string = getState().fetchDefault.baseAPI + 'api/{type}/profileimage';
		if (image.entityType === c.NODE_TYPE_USER) {
			url = url.replace('{type}', 'userprofile');
		} else if (image.entityType === c.NODE_TYPE_SHOP) {
			url = url.replace('{type}', 'shopprofile');
		}

		return fetch(
			url,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newImage: IImageData) => {
						if (image.entityType === c.NODE_TYPE_USER) {
							dispatch(updateUserProfileImage(newImage));
							dispatch(
								notifSend({
									message: i18n.t('Notification_Success_User_Profile_ProfilePicture'),
									kind: 'success',
									dismissAfter: 3000,
								}),
							);
						} else if (image.entityType === c.NODE_TYPE_SHOP) {
							dispatch(updateShopProfileImage(newImage));
							dispatch(
								notifSend({
									message: i18n.t('Notification_Success_Shop_Profile_ProfilePicture'),
									kind: 'success',
									dismissAfter: 3000,
								}),
							);
						}
						dispatch(closeModal());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function updateCoverImage(image: ICropImageData) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		let url: string = getState().fetchDefault.baseAPI + 'api/{type}/coverimage';
		if (image.entityType === c.NODE_TYPE_USER) {
			url = url.replace('{type}', 'userprofile');
		} else if (image.entityType === c.NODE_TYPE_SHOP) {
			url = url.replace('{type}', 'shopprofile');
		}

		return fetch(
			url,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newImage: IImageData) => {
						if (image.entityType === c.NODE_TYPE_USER) {
							dispatch(updateUserCoverImage(newImage));
							dispatch(
								notifSend({
									message: i18n.t('Notification_Success_Shop_Profile_CoverPicture'),
									kind: 'success',
									dismissAfter: 3000,
								}),
							);
						}
						if (image.entityType === c.NODE_TYPE_SHOP) {
							dispatch(updateShopCoverImage(newImage));
							dispatch(
								notifSend({
									message: i18n.t('Notification_Success_Shop_Profile_CoverPicture'),
									kind: 'success',
									dismissAfter: 3000,
								}),
							);
						}
						dispatch(closeModal());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function getOriginalImage(id: string, formName?: string) {
	return (dispatch, getState: () => IStore) => {
		if (!id) {
			return;
		}
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imageupload/getOriginalImage?id=' + id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		)
			.then((res) => {
				if (res.ok) {
					if (formName) {
						return res.json().then((original: IImageData) => {
							dispatch(setOriginalImage(original, formName));
							dispatch(getImageSuccess(original));
						});
					} else {
						return res.json();
					}
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function cropAndSaveFormImage(image: IImageData, fieldName?: string, formName?: string, images?: any[]) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imageupload/crop',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					if (fieldName && formName) {
						return res.json().then((image: IImageData) => {
							images.push({
								id: image.id,
								href: image.href,
								index: images.length,
								description: null,
								author: null,
							});
							dispatch(change(formName, fieldName, images));
							dispatch(closeModal());
						});
					} else {
						return res.json();
					}
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

function updateUserProfileImage(image: IImageData) {
	return (dispatch, getState: () => IStore) => {
		const usercard = getState().usercard.usercard;
		usercard.picture = image.href;
		usercard.pictureId = image.id;
		dispatch(userCardSuccess(usercard));

		const userPref = getState().userPreferences;
		userPref.profilePicture = image.href;
		dispatch(userPreferencesSuccess(userPref));
	};
}

function updateUserCoverImage(image: IImageData) {
	return (dispatch, getState: () => IStore) => {
		const usercard = getState().usercard.usercard;
		usercard.coverPicture = image.href;
		usercard.coverPictureId = image.id;
		dispatch(userCardSuccess(usercard));
	};
}

function updateShopProfileImage(image: IImageData) {
	return (dispatch, getState: () => IStore) => {
		const shopcard = { ...getState().shopcard.shopcard };
		shopcard.picture = image.href;
		dispatch(shopCardSuccess(shopcard));
	};
}

function updateShopCoverImage(image: IImageData) {
	return (dispatch, getState: () => IStore) => {
		const shopcard = { ...getState().shopcard.shopcard };
		shopcard.coverPicture = image.href;
		dispatch(shopCardSuccess(shopcard));
	};
}

function setOriginalImage(image: IImageData, formName: string) {
	return (dispatch) => {
		dispatch(change(formName, 'id', image.id));
		dispatch(change(formName, 'href', image.href));
		dispatch(change(formName, 'url', image.href));
		dispatch(change(formName, 'description', null));
		dispatch(change(formName, 'author', null));
	};
}

/** Action Creator */
export function requestStart(): IAddImageDataAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IAddImageDataAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getImageSuccess(image: IImageData): IAddImageDataAction {
	return {
		type: ADD_IMAGE_SUCCESS,
		payload: {
			image,
		},
	};
}

/** Action Creator */
export function clearAddImageFormError(): IAddImageDataAction {
	return {
		type: CLEAR_FORM_ERROR,
	};
}
